// Pure geometry for the window manager (unit-tested).

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface Area {
  w: number;
  h: number;
}

/** Pixels of title bar that must stay inside the desktop so a window is always recoverable. */
export const GRAB_MARGIN = 96;
export const TITLE_H = 36;

/** Keep size within [min, area] and the title bar reachable. */
export function clampBounds(b: Bounds, area: Area, minW: number, minH: number): Bounds {
  const w = Math.round(Math.max(Math.min(minW, area.w), Math.min(b.w, area.w)));
  const h = Math.round(Math.max(Math.min(minH, area.h), Math.min(b.h, area.h)));
  const x = Math.round(Math.min(Math.max(b.x, GRAB_MARGIN - w), area.w - GRAB_MARGIN));
  const y = Math.round(Math.min(Math.max(b.y, 0), Math.max(0, area.h - TITLE_H)));
  return { x, y, w, h };
}

/**
 * Fit a window fully inside the desktop when it can (used when restoring saved
 * geometry and when the viewport shrinks); falls back to the reachable rule.
 */
export function fitBounds(b: Bounds, area: Area, minW: number, minH: number): Bounds {
  const c = clampBounds(b, area, minW, minH);
  return { ...c, x: Math.max(0, Math.min(c.x, area.w - c.w)), y: Math.max(0, Math.min(c.y, area.h - c.h)) };
}

/** Clamp a position during a drag (size unchanged). */
export function clampPosition(x: number, y: number, w: number, area: Area) {
  return {
    x: Math.min(Math.max(x, GRAB_MARGIN - w), area.w - GRAB_MARGIN),
    y: Math.min(Math.max(y, 0), Math.max(0, area.h - TITLE_H)),
  };
}

export type Edge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** New bounds for a resize drag from `start` by (dx, dy) on the given edge. */
export function resizeBounds(start: Bounds, edge: Edge, dx: number, dy: number, minW: number, minH: number, area: Area): Bounds {
  let { x, y, w, h } = start;
  if (edge.includes('e')) w = Math.min(Math.max(minW, start.w + dx), area.w - start.x);
  if (edge.includes('s')) h = Math.min(Math.max(minH, start.h + dy), area.h - start.y);
  if (edge.includes('w')) {
    const nx = Math.max(0, Math.min(start.x + dx, start.x + start.w - minW));
    w = start.w + (start.x - nx);
    x = nx;
  }
  if (edge.includes('n')) {
    const ny = Math.max(0, Math.min(start.y + dy, start.y + start.h - minH));
    h = start.h + (start.y - ny);
    y = ny;
  }
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}

/** Default placement: centred in the free area right of the icon column, cascaded. */
export function defaultBounds(w: number, h: number, area: Area, cascade: number, leftReserve = 220): Bounds {
  const step = (cascade % 7) * 26;
  const free = Math.max(0, area.w - leftReserve);
  const ww = Math.min(w, area.w - 16);
  const hh = Math.min(h, area.h - 16);
  const x = area.w > ww + leftReserve + 20 ? leftReserve + Math.max(0, (free - ww) / 2) - 40 + step : (area.w - ww) / 2;
  const y = Math.max(8, (area.h - hh) / 2 - 30 + step);
  return clampBounds({ x, y, w: ww, h: hh }, area, 200, 150);
}
